import { useRouter } from 'next/router'

import Link from '../../atoms/Link/Link'
import analyticsAttributes, { Action, Category } from '../../../util/analytics-data-attributes/analyticsAttributes'
import Colors from '../../theme/colors'
import DashboardSvg from '../../atoms/Icons/General/Dashboard'
import DashboardSolidSvg from '../../atoms/Icons/General/DashboardSolid'
import GraphBarSvg from '../../atoms/Icons/General/GraphBar'
import GraphBarSolidSvg from '../../atoms/Icons/General/GraphBarSolid'
import ShoppingCartSvg from '../../atoms/Icons/General/ShoppingCart'
import ShoppingCartSolidSvg from '../../atoms/Icons/General/ShoppingCartSolid'
import PlusIcon from '../../atoms/Icons/Button/Plus'
import StoreSvg from '../../atoms/Icons/General/Store'
import StoreSolidSvg from '../../atoms/Icons/General/StoreSolid'
import UsersSvg from '../../atoms/Icons/General/Users'
import UsersSolidSvg from '../../atoms/Icons/General/UsersSolid'
import VacancySvg from '../../atoms/Icons/General/Vacancy'
import VacancySolidSvg from '../../atoms/Icons/General/VacancySolid'
import WalletSvg from '../../atoms/Icons/General/Wallet'
import WalletSolidSvg from '../../atoms/Icons/General/WalletSolid'
import { AttentionBadge, NumberBadge, StyledButton, Ul } from './Nav.styles'
import UserSvg from '../../atoms/Icons/Button/User'
import { Job } from '../../../state/reducers/jobs/jobs'

export const paths = {
  overzicht: {
    title: 'Overzicht',
    href: '/',
  },
  producttegoed: {
    title: 'Producttegoed',
    href: '/producttegoed',
  },
  vacatures: {
    title: 'Mijn Vacatures',
    href: '/vacatures',
  },
  statistieken: {
    title: 'Mijn Statistieken',
    href: '/statistieken',
  },
  kandidaten: {
    title: 'Zoek Kandidaten',
    href: '/recruiter',
  },
  webshop: {
    title: 'Webshop',
    href: '/webshop',
  },
  winkelwagen: {
    title: 'Winkelwagen',
    href: '/webshop/cart',
  },
}

export type NavProps = {
  onClickPlaceVacancy: () => void
  clickPlaceVacancyIsInProgress: boolean
  isLoggedIn: boolean
  onLogIn?: () => void
  onLogOut?: () => void
  className?: string
  itemsInCart?: number
  lastRejectedJob?: Job
}

const Nav: React.FunctionComponent<NavProps> = ({
  clickPlaceVacancyIsInProgress,
  onClickPlaceVacancy,
  onLogIn,
  onLogOut,
  isLoggedIn,
  className,
  itemsInCart,
  lastRejectedJob,
}) => {
  const router = useRouter()
  
  const pathName = router?.pathname
  const Dashboard = <DashboardSvg color={Colors.Primary.GreyTextLight} />
  const DashboardSelected = <DashboardSolidSvg color={Colors.Primary.White} />
  const Wallet = <WalletSvg color={Colors.Primary.GreyTextLight} />
  const WalletSelected = <WalletSolidSvg color={Colors.Primary.White} />
  const Vacancy = <VacancySvg color={Colors.Primary.GreyTextLight} />
  const VacancySelected = <VacancySolidSvg color={Colors.Primary.White} />
  const GraphBar = <GraphBarSvg color={Colors.Primary.GreyTextLight} />
  const GraphBarSelected = <GraphBarSolidSvg color={Colors.Primary.White} />
  const User = <UserSvg color={Colors.Primary.GreyTextLight} />
  const Users = <UsersSvg color={Colors.Primary.GreyTextLight} />
  const UsersSelected = <UsersSolidSvg color={Colors.Primary.White} />
  const Store = <StoreSvg color={Colors.Primary.GreyTextLight} />
  const StoreSelected = <StoreSolidSvg color={Colors.Primary.White} />
  const ShoppingCart = <ShoppingCartSvg color={Colors.Primary.GreyTextLight} />
  const ShoppingCartSelected = <ShoppingCartSolidSvg color={Colors.Primary.White} />

  return (
    <nav data-testid="nav" className={className}>
      <Ul>
        <li>
          <StyledButton
            onClick={() => onClickPlaceVacancy()}
            isInProgress={clickPlaceVacancyIsInProgress}
            data-testid="create-draft-button"
            {...analyticsAttributes({
              router,
              action: Action.jpfPlaceVacancy,
              category: Category.navigationMenuLeftbar,
            })}
          >
            {!clickPlaceVacancyIsInProgress && <PlusIcon />}
            Plaats Vacature
          </StyledButton>
        </li>
        <li>
          <Link
            href={paths.overzicht.href}
            isSelected={pathName === paths.overzicht.href}
            {...analyticsAttributes({
              router,
              action: Action.clickDashboard,
              category: Category.navigationMenuLeftbar,
              label: paths.overzicht.href,
            })}
          >
            {pathName === paths.overzicht.href ? DashboardSelected : Dashboard}
            {paths.overzicht.title}
          </Link>
        </li>
        <li>
          <Link
            href={paths.producttegoed.href}
            isSelected={pathName === paths.producttegoed.href}
            {...analyticsAttributes({
              router,
              action: Action.clickProductEntitlements,
              category: Category.navigationMenuLeftbar,
              label: paths.producttegoed.href,
            })}
          >
            {pathName === paths.producttegoed.href ? WalletSelected : Wallet}
            {paths.producttegoed.title}
          </Link>
        </li>
        <li>
          <Link
            href={paths.vacatures.href}
            isSelected={pathName === paths.vacatures.href}
            {...analyticsAttributes({
              router,
              action: Action.clickJobs,
              category: Category.navigationMenuLeftbar,
              label: paths.vacatures.href,
            })}
          >
            {pathName === paths.vacatures.href ? VacancySelected : Vacancy}
            {paths.vacatures.title}
            {lastRejectedJob ? (
              <AttentionBadge data-testid="attention-badge">!</AttentionBadge>
            ) : null}
          </Link>
        </li>
        <li>
          <Link
            href={paths.statistieken.href}
            isSelected={pathName === paths.statistieken.href}
            {...analyticsAttributes({
              router,
              action: Action.clickStatistics,
              category: Category.navigationMenuLeftbar,
              label: paths.statistieken.href,
            })}
          >
            {pathName === paths.statistieken.href ? GraphBarSelected : GraphBar}
            {paths.statistieken.title}
          </Link>
        </li>
        <li>
          <a
            href={paths.kandidaten.href}
            {...analyticsAttributes({
              router,
              action: Action.clickProfiles,
              category: Category.navigationMenuLeftbar,
              label: paths.kandidaten.href,
            })}
          >
            {pathName === paths.kandidaten.href ? UsersSelected : Users}
            {paths.kandidaten.title}
          </a>
        </li>
        <hr />
        <li>
          <Link
            href={paths.webshop.href}
            isSelected={pathName === paths.webshop.href}
            {...analyticsAttributes({
              router,
              action: Action.clickWebshop,
              category: Category.navigationMenuLeftbar,
              label: paths.webshop.href,
            })}
          >
            <span>{pathName === paths.webshop.href ? StoreSelected : Store}</span>
            {paths.webshop.title}
          </Link>
        </li>
        <li>
          <Link
            href={paths.winkelwagen.href}
            isSelected={pathName === paths.winkelwagen.href}
            {...analyticsAttributes({
              router,
              action: Action.clickCart,
              category: Category.navigationMenuLeftbar,
              label: paths.winkelwagen.href,
            })}
          >
            {pathName === paths.winkelwagen.href ? ShoppingCartSelected : ShoppingCart}
            {paths.winkelwagen.title}
            {itemsInCart > 0 ? (
              <NumberBadge data-testid="number-badge">{itemsInCart}</NumberBadge>
            ) : null}
          </Link>
        </li>
        {onLogIn && (
          <>
            <hr />
            {isLoggedIn ? (
              <li>
                <a onClick={() => onLogOut()}>{User} Uitloggen</a>
              </li>
            ) : (
              <li>
                <a onClick={() => onLogIn()}>
                  {User}
                  Inloggen
                </a>
              </li>
            )}
          </>
        )}
      </Ul>
    </nav>
  )
}

export default Nav